import { useState, useEffect } from "react";
import styles from "./WorkOrderTab.module.scss";
import classNames from "classnames/bind";
import WorkOrderList from "./component/list/WorkOrderList";
import WorkOrderDetails from "./component/details/WorkOrderDetails";
import WorkOrderCreate from "./component/create/WorkOrderCreate";

const cx = classNames.bind(styles);

interface WorkOrderTabProps {
  jobId: string;
  onDetailsOpenChange?: (isOpen: boolean) => void;
  onRegisterBackFromDetails?: (back: (() => void) | null) => void;
}

const WorkOrderTab = ({ jobId, onDetailsOpenChange, onRegisterBackFromDetails }: WorkOrderTabProps) => {
  const [currentView, setCurrentView] = useState("list");
  const [selectedWorkOrderId, setSelectedWorkOrderId] = useState<string | null>(null);
  const [type, setType] = useState<"create" | "edit" | null>(null);

  const handleCreateWorkOrder = () => {
    setCurrentView("create");
    setType("create");
  };

  const handleViewWorkOrder = (workOrderId: string) => {
    setCurrentView("details");
    setSelectedWorkOrderId(workOrderId);
  };

  const handleEditWorkOrder = (workOrderId: string) => {
    setCurrentView("create");
    setSelectedWorkOrderId(workOrderId);
    setType("edit");
  };

  const handleDeleteWorkOrder = (workOrderId: string) => {
    setCurrentView("list");
  };

  useEffect(() => {
    onDetailsOpenChange?.(currentView === "details");
  }, [currentView, onDetailsOpenChange]);

  useEffect(() => {
    if (currentView === "details") {
      onRegisterBackFromDetails?.(() => {
        setCurrentView("list");
        setSelectedWorkOrderId(null);
      });
    } else {
      onRegisterBackFromDetails?.(null);
    }
    return () => onRegisterBackFromDetails?.(null);
  }, [currentView, onRegisterBackFromDetails]);

  const handleCreateWorkOrderSuccess = () => {
    setCurrentView("list");
  };

  const handleCancelCreateWorkOrder = () => {
    setCurrentView("list");
  };

  const renderTabContent = () => {
    switch (currentView) {
      case "list":
        return (
          <WorkOrderList
            jobId={jobId}
            onCreateWorkOrder={handleCreateWorkOrder}
            onViewWorkOrder={handleViewWorkOrder}
            onEditWorkOrder={handleEditWorkOrder}
            onDeleteWorkOrder={handleDeleteWorkOrder}
          />
        );
      case "details":
        return <WorkOrderDetails workOrderId={selectedWorkOrderId || ''} onEditWorkOrder={() => handleEditWorkOrder(selectedWorkOrderId || '')} />;
      case "create":
        return (
          <WorkOrderCreate
            jobId={jobId}
            type={type as "create" | "edit"}
            onSuccess={handleCreateWorkOrderSuccess}
            onCancel={handleCancelCreateWorkOrder}
            workOrderId={selectedWorkOrderId}
          />
        );
      default:
        return null;
    }
  };

  return (
    <div className={cx("work-order-tab-container")}>{renderTabContent()}</div>
  );
};

export default WorkOrderTab;
